<template>
  <v-row>
    <v-col cols="12" lg="12">
      <Aggregate
        title="Markets"
        v-model="computedInput.markets"
        :error-messages="$utils.getIfExists(errors, ['input', 'market'])"
        :ItemConstructor="marketFactory"
        :headers="headers"
        dense
        v-bind="$attrs"
        @change="$emit('change')"
      >
        <template #item.bus_id="{ item }">
          <v-select
            v-model="item.bus_id"
            :items="nodeIndex"
            :label="schema.bus_id.title"
            single-line
            dense
            @change="$emit('change')"
          >
            <template #selection="data">
              {{ data.item.name }}
              <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
            </template>
            <template #item="data">
              {{ data.item.name }}
              <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
            </template>
          </v-select>
        </template>
        <template #default="{ item, errors }">
          <v-card class="pa-2 ma-2" flat>
            <v-card-text>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">General</p>
                <v-switch
                  v-model="item.constant_price"
                  :label="schema.constant_price.title"
                  :hint="schema.constant_price.tooltip"
                  persistent-hint
                  :error-messages="$utils.getIfExists(errors, ['constant_price', 'messages'])"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <InputYearly
                  v-model="item.price"
                  v-if="item.constant_price"
                  :label="schema.price.title"
                  :hint="schema.price.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['price', 'messages'])"
                  :suffix="schema.price.unit"
                  type="number"
                  :num_years="num_years"
                  fill
                  :fill-value="schema.price.items.default"
                  :min="schema.price.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputYearlyProfile
                  v-model="item.price_profiles"
                  v-else
                  :label="schema.price_profiles.title"
                  :hint="schema.price_profiles.tooltip"
                  :suffix="schema.price.unit"
                  :error-messages="$utils.getIfExists(errors, ['price_profiles'])"
                  :num_years="num_years"
                  :profile-type="schema.price_profiles.profileType"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <v-switch
                  v-model="item.constant_buy"
                  :label="schema.constant_buy.title"
                  :hint="schema.constant_buy.tooltip"
                  persistent-hint
                  :error-messages="$utils.getIfExists(errors, ['constant_buy', 'messages'])"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <InputYearly
                  v-model="item.buy_limit"
                  v-if="item.constant_buy"
                  label="Hourly Purchase Limit "
                  :hint="schema.buy_limit.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['buy_limit', 'messages'])"
                  :suffix="schema.buy_limit.unit"
                  type="number"
                  :num_years="num_years"
                  fill
                  :fill-value="schema.buy_limit.items.default"
                  :min="schema.buy_limit.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputYearlyProfile
                  v-model="item.buy_limit_profiles"
                  v-else
                  label="Hourly Purchase Limit Profile "
                  :hint="schema.buy_limit_profiles.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['buy_limit_profiles'])"
                  :num_years="num_years"
                  :suffix="schema.buy_limit.unit"
                  :profile-type="schema.buy_limit_profiles.profileType"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <v-switch
                  v-model="item.constant_sell"
                  label="Constant Sale Limit"
                  :hint="schema.constant_sell.tooltip"
                  persistent-hint
                  :error-messages="$utils.getIfExists(errors, ['constant_sell', 'messages'])"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <InputYearly
                  v-model="item.sell_limit"
                  v-if="item.constant_sell"
                  label="Hourly Sale Limit"
                  :hint="schema.sell_limit.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['sell_limit', 'messages'])"
                  :suffix="schema.sell_limit.unit"
                  type="number"
                  :num_years="num_years"
                  fill
                  :fill-value="schema.sell_limit.items.default"
                  :min="schema.sell_limit.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputYearlyProfile
                  v-model="item.sell_limit_profiles"
                  v-else
                  label="Hourly Sale Limit Profile"
                  :hint="schema.sell_limit_profiles.tooltip"
                  :suffix="schema.sell_limit.unit"
                  :error-messages="$utils.getIfExists(errors, ['sell_limit_profiles'])"
                  :num_years="num_years"
                  :profile-type="schema.sell_limit_profiles.profileType"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputYearly
                  v-model="item.net_sell_limits"
                  label="Net Sales Limits"
                  :hint="schema.net_sell_limits.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['net_sell_limits', 'messages'])"
                  :suffix="schema.net_sell_limits.unit"
                  type="number"
                  :num_years="num_years"
                  fill
                  :fill-value="schema.net_sell_limits.items.default"
                  :min="schema.net_sell_limits.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputYearly
                  v-model="item.net_buy_limits"
                  label="Net Purchases Limits"
                  :hint="schema.net_buy_limits.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['net_buy_limits', 'messages'])"
                  :suffix="schema.net_buy_limits.unit"
                  type="number"
                  :num_years="num_years"
                  fill
                  :fill-value="schema.net_buy_limits.items.default"
                  :min="schema.net_buy_limits.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputTextField
                  v-model.number="item.firm_capacity"
                  :label="schema.firm_capacity.title"
                  :hint="schema.firm_capacity.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['firm_capacity', 'messages'])"
                  :suffix="schema.firm_capacity.unit"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>

                <v-switch
                  v-model="item.ppa.active"
                  label="Power Purchase Agreement"
                  hint="A price for inputs and exports"
                  persistent-hint
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.imports"
                  object-value
                  fill
                  toggle-items-type
                  label="imports PPA price"
                  suffix="$/MWh"
                  :num_years="num_years"
                  profile-type="PPA_ENERGY_PRICE"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.exports"
                  object-value
                  fill
                  toggle-items-type
                  label="exports PPA price"
                  suffix="$/MWh"
                  :num_years="num_years"
                  profile-type="PPA_ENERGY_PRICE"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <v-divider class="mt-4"></v-divider>
              </v-card>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Power Flow Parameters</p>
                <v-switch
                  v-model="item.active"
                  :label="schema.active.title"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <InputTextField
                  v-model.number="item.voltage_set_point"
                  :label="schema.voltage_set_point.title"
                  :suffix="schema.voltage_set_point.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <InputTextField
                  v-model.number="item.angle_set_point"
                  :label="schema.angle_set_point.title"
                  :suffix="schema.angle_set_point.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <v-divider class="mt-4"></v-divider>
              </v-card>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Short Circuit Parameters</p>
                <InputTextField
                  v-model.number="item.max_SC_power_provision"
                  :label="schema.max_SC_power_provision.title"
                  :suffix="schema.max_SC_power_provision.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <InputTextField
                  v-model.number="item.min_SC_power_provision"
                  :label="schema.min_SC_power_provision.title"
                  :suffix="schema.min_SC_power_provision.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <InputTextField
                  v-model.number="item.max_SC_impedance_rx_ratio"
                  :label="schema.max_SC_impedance_rx_ratio.title"
                  :suffix="schema.max_SC_impedance_rx_ratio.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <InputTextField
                  v-model.number="item.min_SC_impedance_rx_ratio"
                  :label="schema.min_SC_impedance_rx_ratio.title"
                  :suffix="schema.min_SC_impedance_rx_ratio.unit"
                  type="number"
                  @change="$emit('change')"
                />
              </v-card>
            </v-card-text>
          </v-card>
        </template>
      </Aggregate>
    </v-col>
  </v-row>
</template>

<script>
import { CaseModel, schemas } from "data-dictionary";

export default {
  name: "SystemMarket",
  components: {
    Aggregate: () => import("@/components/Aggregate.vue"),
    InputTextField: () => import("@/components/inputs/InputTextField.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
  },
  props: {
    num_years: {
      type: Number,
    },
    input: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      headers: CaseModel.Market.HEADERS,
      schemas,
      schema: {
        ...schemas.filter((v) => v.$id === "market.schema.json")[0].properties,
        ...schemas
          .filter((v) => v.$id === "market.schema.json")[0]
          .allOf.map((v) => v.then.properties)
          .reduce((obj, item) => ({ ...obj, ...item }), {}),
      },
    };
  },
  computed: {
    nodeIndex() {
      return this.computedInput.nodes
        ? this.computedInput.nodes.map((el) => ({
            name: el.name,
            value: el.id,
          }))
        : [];
    },
    computedInput: {
      get() {
        return this.input;
      },
      set(val) {
        this.$emit("update:input", val);
        this.$emit("change");
      },
    },
  },
  watch: {},
  methods: {
    marketFactory: CaseModel.Market,
  },
};
</script>

<style></style>
